/**
 * The two resend paths — the user asking for a message to go out again.
 *
 *   - `retryMessage` — the "Not sent · Retry" affordance on ONE failed bubble.
 *   - `retryConnection` — the connection-level Retry, which resends the task's
 *     last user message when the socket died mid-turn.
 *
 * Both resend under the ORIGINAL client `id`: that is the server's inbox dedup
 * key, so a retry of a processed-but-unacked send is acked success without a
 * duplicate turn or a re-billed run.
 */

import { useModeStore } from "../modeStore";
import { useRuntimeStore } from "../runtimeStore";
import { ensureConnected, getSocket } from "../socketService";
import { findMessageByAnyId } from "../helpers/findMessage";
import { useConnectionStore } from "../connectionStore";
import { emitUserMessage, notRecordedCode } from "./emitUserMessage";
import { DEFAULT_AGENT, type Message, type TaskState } from "../types";
import type { TaskStoreSeam } from "../taskStoreTypes";

/**
 * A resend's wire payload. Same client `id` on purpose: it is the server's
 * dedup key. The TASK's locked engine/model rides it, falling back to the
 * global selector only if the task somehow lacks one.
 */
function resendPayload(
  taskId: string,
  task: TaskState,
  messageId: string,
  text: string
): Record<string, unknown> {
  return {
    taskId,
    sessionId: taskId,
    message: text,
    id: messageId,
    targetAgent: DEFAULT_AGENT,
    mode: useModeStore.getState().mode,
    runtime: task.runtime ?? useRuntimeStore.getState().runtime,
    model: task.model,
  };
}

/** The message's typed text, or null if it carries none (attachment-only). */
function textOf(message: Message): string | null {
  const textBlock = message.content.find((b) => b.type === "text");
  return textBlock && textBlock.type === "text" ? textBlock.text : null;
}

/** Flip a task's phase, tolerating its absence from the Map. */
function setPhase(
  seam: TaskStoreSeam,
  taskId: string,
  phase: TaskState["phase"]
): void {
  seam.get()._mutateTasks((tasks) => {
    const t = tasks.get(taskId);
    if (t) t.phase = phase;
  });
}

export function retryMessage(seam: TaskStoreSeam, messageId: string): void {
  const { get } = seam;
  ensureConnected();
  const socket = getSocket();
  const { selectedTaskId, tasks } = get();
  if (!selectedTaskId) return;
  const task = tasks.get(selectedTaskId);
  if (!task) return;

  const failedIdx = findMessageByAnyId(task.messages, messageId);
  const failedMsg =
    failedIdx !== -1 && task.messages[failedIdx].sendFailed
      ? task.messages[failedIdx]
      : null;
  if (!failedMsg) return;

  const text = textOf(failedMsg);
  if (text === null) return;

  get()._mutateTasks((tasks) => {
    const t = tasks.get(selectedTaskId);
    if (!t) return;
    t.phase = "waiting";
    const idx = findMessageByAnyId(t.messages, messageId);
    if (idx !== -1) {
      t.messages[idx].sendFailed = false;
      t.messages[idx].sendFailedCode = undefined;
    }
  });

  emitUserMessage(
    socket,
    resendPayload(selectedTaskId, task, messageId, text),
    (ack) => {
      if (ack?.success) return;
      get()._mutateTasks((tasks) => {
        const t = tasks.get(selectedTaskId);
        if (!t) return;
        t.phase = "idle";
        const idx = findMessageByAnyId(t.messages, messageId);
        if (idx !== -1) {
          t.messages[idx].sendFailed = true;
          t.messages[idx].sendFailedCode = notRecordedCode(ack);
        }
      });
      console.warn("[retryMessage] Failed:", ack?.error ?? "transport failure");
    }
  );
}

export function retryConnection(seam: TaskStoreSeam): void {
  const { get } = seam;
  ensureConnected();
  const { selectedTaskId, tasks } = get();
  const socket = getSocket();
  if (!selectedTaskId) return;
  const task = tasks.get(selectedTaskId);
  if (!task) return;

  // The disconnect handler resets phase to 'idle' before the user clicks Retry.
  // Check connection state (error/offline) OR task phase — not just phase alone.
  const connStatus = useConnectionStore.getState().connectionState.status;
  const isDisconnected = connStatus === "error" || connStatus === "offline";
  const isStuck = task.phase === "processing" || task.phase === "waiting";
  if (!isStuck && !isDisconnected) return;

  const lastUserMessage = [...task.messages]
    .reverse()
    .find((m) => m.role === "user");
  if (!lastUserMessage) return;

  const text = textOf(lastUserMessage);
  if (text === null) return;

  setPhase(seam, selectedTaskId, "waiting");

  emitUserMessage(
    socket,
    resendPayload(selectedTaskId, task, lastUserMessage.id, text),
    (ack) => {
      if (ack?.success) return;
      // A MISSING ack is a TRANSPORT failure, and this path is reached BECAUSE
      // the connection is broken — so its ack is the likeliest to go missing,
      // not the least. This used to emit through a bare `socket?.emit` (no ack
      // timeout) and test `if (ack && !ack.success)`, which read a missing ack
      // as success: a null socket, a dropped emit, or a server that never
      // answered all left the task pinned in the 'waiting' set above, spinning
      // with no Retry affordance and no way back to idle.
      setPhase(seam, selectedTaskId, "idle");
      console.warn(
        "[retryConnection] Failed:",
        ack?.error ?? "transport failure"
      );
    }
  );
}
